import { SubscriptionDto } from "../../../types/subscription";

const fmtMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const readableFrequency = (f: number) => (f === 12 ? "MONTHLY" : "YEARLY");

// Mirrors the legacy admin lead view's "Promo applied: <name> (<desc>)" — the
// human-readable promo descriptor is the operationally meaningful value (some
// promos exist without a coupon code, so the raw promoId is empty for them).
// Falls back to the coupon code when only that is present, then to "—".
const promoApplied = (s: SubscriptionDto): string => {
  if (s.promoName) return s.promoName;
  if (s.promoId) return s.promoId;
  return "—";
};

export default function SubscriptionDataCard({
  subscription,
}: {
  subscription: SubscriptionDto;
}) {
  const traits = Object.entries(subscription.capitalsByTrait ?? {});
  return (
    <section
      style={{
        background: "white",
        borderRadius: "0.6rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        borderLeft: "3px solid #0069d9",
        padding: "1rem 1.1rem",
        marginBottom: "1rem",
      }}
    >
      <h3
        style={{
          margin: "0 0 0.6rem",
          fontSize: ".78rem",
          fontWeight: 700,
          color: "#1f2933",
          textTransform: "uppercase",
          letterSpacing: ".4px",
        }}
      >
        Datos de la suscripción
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "0.65rem 1.25rem",
        }}
      >
        <Field label="Capital" value={fmtMoney(subscription.capital)} />
        <Field label="Net Premium" value={fmtMoney(subscription.net)} />
        <Field label="Total Premium" value={fmtMoney(subscription.total)} />
        <Field label="Surcharges" value={fmtMoney(subscription.surcharge)} />
        <Field label="Extracharges" value={fmtMoney(subscription.extracharge)} />
        <Field label="Taxes" value={fmtMoney(subscription.taxes)} />
        <Field label="Frequency" value={readableFrequency(subscription.frequency)} />
        <Field label="Annuality" value={String(subscription.annuality)} />
        <Field label="Effect Date" value={fmtDate(subscription.effect_date)} />
        <Field label="Purchase Date" value={fmtDate(subscription.purchase_date)} />
        <Field label="Renewal Date" value={fmtDate(subscription.renewal_date)} />
        <Field label="Pause Date" value={fmtDate(subscription.pauseDate)} />
        <Field
          label="Promo applied"
          value={promoApplied(subscription)}
          hint={subscription.promoDescription ?? undefined}
        />
        <Field label="Coverage" value={subscription.coverage || "—"} />
        <Field label="Carrier" value={subscription.carrier} />
        <Field label="Birthdate (lead)" value={fmtDate(subscription.birth_date)} />
        <Field
          label="Default payment method"
          value={subscription.defaultPaymentMethod || "—"}
          mono
        />
        <Field
          label="Payment external ID"
          value={subscription.paymentExternalId || "—"}
          mono
        />
        <Field
          label="Has unpaid invoice"
          value={subscription.hasUnpayedInvoice ? "Sí" : "No"}
        />
        <Field
          label="Has paused date"
          value={subscription.hasPausedDate ? "Sí" : "No"}
        />
      </div>
      {traits.length > 0 && (
        <div style={{ marginTop: "0.85rem" }}>
          <h4
            style={{
              margin: "0 0 0.4rem",
              fontSize: ".72rem",
              fontWeight: 700,
              color: "#73879C",
              textTransform: "uppercase",
              letterSpacing: ".4px",
            }}
          >
            Capitales por trait
          </h4>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {traits.map(([trait, cap]) => (
              <span
                key={trait}
                style={{
                  display: "inline-flex",
                  gap: "0.4rem",
                  alignItems: "center",
                  padding: "0.2rem 0.6rem",
                  borderRadius: "0.4rem",
                  background: "#f8f9fb",
                  border: "1px solid #dee2e6",
                  fontSize: ".78rem",
                }}
              >
                <strong style={{ color: "#73879C" }}>{trait}</strong>
                <span style={{ color: "#1f2933", fontWeight: 600 }}>
                  {fmtMoney(cap)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  mono,
  hint,
}: {
  label: string;
  value: string;
  mono?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: ".7rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: ".88rem",
          color: "#1f2933",
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-word",
        }}
        title={hint}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: ".7rem",
            color: "#73879C",
            fontStyle: "italic",
            marginTop: ".15rem",
            wordBreak: "break-word",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
